import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import SwalDialog from 'lib/utils/toast';
import Select from 'react-select/creatable';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  salmon,
  squidInk,
  white,
} from 'lib/css/colors';
import { selectStyles } from 'lib/css/selectStyles';
import { BRANDS_MERGE } from 'lib/networking/endpoints';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import usePost from 'lib/hooks/usePost';

function MergeBrandsModal({ mergeList, setMergeList, onHide, onMerge = () => {} }) {
  const [filteredBrands, setFilteredBrands] = useState(mergeList);
  const [parentBrand, setParentBrand] = useState(null);

  useEffect(() => {
    //If there is only one verified brand in mergeList it will be set as the parent brand.
    const verifiedBrand = mergeList.find(
      brand => brand.verification_status === BrandVerificationStatus.VERIFIED,
    );
    const unverifiedBrands = mergeList.filter(
      brand => brand.verification_status !== BrandVerificationStatus.VERIFIED,
    );

    if (
      verifiedBrand &&
      mergeList.filter(brand => brand.verification_status === BrandVerificationStatus.VERIFIED)
        .length === 1
    ) {
      setFilteredBrands(unverifiedBrands);
      setParentBrand(verifiedBrand);
    }
  }, [mergeList]);

  const handleParentBrandChange = option => {
    if (option) {
      const selectedParent = mergeList.find(brand => brand.id === option.value);
      setParentBrand(selectedParent);
      setFilteredBrands(mergeList.filter(mergeBrand => mergeBrand.id !== option.value));
    } else {
      setParentBrand(null);
      setFilteredBrands(mergeList);
    }
  };

  const handleRemoveBrand = brandId => {
    setFilteredBrands(filteredBrands.filter(mergeBrand => mergeBrand.id !== brandId));
    setMergeList(mergeList.filter(mergeBrand => mergeBrand.id !== brandId));
  };

  const { postData: mergeBrands, loading } = usePost(
    BRANDS_MERGE,
    () => {
      SwalDialog('success', 'Successfully merged brands', 'Success', 'center');
      onMerge();
      onHide();
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while merging brands: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const getBrandLabel = brand => {
    const manufacturerName = brand?.global_manufacturer?.name;
    return manufacturerName ? `${brand.name} by ${manufacturerName}` : brand.name;
  };

  const options = mergeList.map(brand => ({
    label: getBrandLabel(brand),
    value: brand.id,
  }));

  const handleMergeBrands = () => {
    const hasVerifiedBrand = filteredBrands.some(
      brand => brand.verification_status === BrandVerificationStatus.VERIFIED,
    );

    if (parentBrand?.verification_status !== BrandVerificationStatus.VERIFIED) {
      Swal.fire({
        icon: 'error',
        title: 'Parent Brand Not Verified',
        text: 'Parent brand is not verified.',
        confirmButtonText: 'OK',
      });
    } else if (hasVerifiedBrand) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Merge',
        text: 'At least one verified brand in the secondary brands.',
        confirmButtonText: 'OK',
      });
    } else {
      mergeBrands({
        parentId: parentBrand.id,
        mergeIds: filteredBrands.map(brand => brand.id),
      });
    }
  };

  return (
    <Modal show={true} onHide={onHide} size="lg" centered>
      <div className={css(styles.modalContent)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Merge Brands</h2>
          <Button
            variant="link"
            className={css(styles.closeButton)}
            onClick={onHide}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <Modal.Body className={css(styles.body)}>
          <div className={css(styles.section)}>
            <h4 className={css(styles.sectionTitle)}>Parent Brand</h4>
            <Select
              options={options}
              value={parentBrand ? options.find(option => option.value === parentBrand.id) : null}
              onChange={handleParentBrandChange}
              placeholder="Select the parent brand"
              styles={selectStyles}
            />
          </div>

          <div className={css(styles.section)}>
            <h4 className={css(styles.sectionTitle)}>Secondary Brands</h4>
            <div className={css(styles.tableWrapper)}>
              <Table className={css(styles.table)}>
                <thead className={css(styles.tableHead)}>
                  <tr>
                    <th>Name</th>
                    <th>FSA ID</th>
                    <th>Product Count</th>
                    <th>Manufacturer</th>
                    <th>Verified</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBrands.map((brand, index) => (
                    <tr key={index} className={css(styles.tableRow)}>
                      <td className={css(styles.brandName)}>{brand.name}</td>
                      <td>{brand.id}</td>
                      <td>{brand?.product_count ?? '-'}</td>
                      <td>{brand?.global_manufacturer?.name || '-'}</td>
                      <td>
                        <span
                          className={css(
                            styles.badge,
                            brand.verification_status === BrandVerificationStatus.VERIFIED
                              ? styles.badgeVerified
                              : styles.badgeUnverified,
                          )}
                        >
                          {brand.verification_status === BrandVerificationStatus.VERIFIED
                            ? 'Yes'
                            : 'No'}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className={css(styles.removeButton)}
                          onClick={() => handleRemoveBrand(brand.id)}
                          aria-label="Remove brand"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
        <div className={css(styles.footer)}>
          <Button
            className={css(styles.mergeButton)}
            disabled={filteredBrands?.length < 1 || loading || !parentBrand}
            onClick={handleMergeBrands}
          >
            {loading ? <Spinner animation="border" role="status" size="sm" /> : 'Merge Brands'}
          </Button>
          <Button className={css(styles.cancelButton)} onClick={() => onHide()}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${greyStroke}`,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 6,
    ':hover': {
      backgroundColor: `${grape}15`,
      color: grape,
    },
  },
  body: {
    padding: '24px',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 12,
  },
  tableWrapper: {
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  table: {
    marginBottom: 0,
  },
  tableHead: {
    backgroundColor: backgroundGrey5,
  },
  tableRow: {
    ':hover': {
      backgroundColor: `${grape}05`,
    },
  },
  brandName: {
    fontWeight: 500,
    color: squidInk,
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeVerified: {
    backgroundColor: '#d1fae5',
    color: '#059669',
  },
  badgeUnverified: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: salmon,
    cursor: 'pointer',
    padding: 6,
    borderRadius: 4,
    ':hover': {
      backgroundColor: `${salmon}15`,
    },
  },
  footer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${greyStroke}`,
    backgroundColor: backgroundGrey5,
  },
  mergeButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

MergeBrandsModal.propTypes = {
  mergeList: PropTypes.array,
  setMergeList: PropTypes.func,
  onHide: PropTypes.func,
  onMerge: PropTypes.func,
};

export default MergeBrandsModal;
